import React from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../../pages/aai/useAsync';
import type { PlanAndUsageData } from '../../types/aai';
import { Banner, Chip, NotMeasured, Stat } from '../aa';
import PlanAndUsage from '../../pages/partner/PlanAndUsage';

/*
 * The AI Champion's Plan & usage. The champion runs the program against the
 * relationship the Owner holds, so this panel reads capacity and commitments
 * and leaves every commercial figure to the Owner. The commercial handoff
 * below is the quote queue, which carries counts and no prices.
 */

export const ChampionPlanPanel: React.FC<{ clientId: string }> = ({ clientId }) => {
  const plan = useAsync<PlanAndUsageData>(() => aaiApi.getPlanUsage(), 'Could not load the plan.');
  const p = plan.data;
  const seats = p?.plan.seats ?? null;
  const used = p?.seats_used ?? 0;
  const commitments = p?.accepted_value?.quotes ?? 0;

  let capacity: React.ReactNode = <NotMeasured reason="Loading" />;
  let capacityFoot = 'Seats in use against the plan';
  if (p && seats) {
    capacity = `${Math.round((used / seats) * 100)}%`;
    capacityFoot = `${used} of ${seats} seats in use`;
  }
  if (p && !seats) {
    capacity = `${used}`;
    capacityFoot = 'People enrolled · no seat limit recorded';
  }
  let title: React.ReactNode = 'Plan not recorded';
  if (p?.plan.display_name) title = p.plan.display_name;

  return (
    <div className="mx-auto max-w-[1000px] space-y-5">
      <Banner
        tone="quiet"
        size="lg"
        kicker="Current relationship"
        title={title}
        desc="Managed portfolio · pooled care · governed improvement. This relationship sets service coverage. Every Project keeps its approved scope and commercial treatment. Delivery Projects receive a separate quote."
        action={<Chip tone="ok">Active</Chip>}
      />
      <div className="grid gap-3 md:grid-cols-3">
        <Stat label="Production capacity" value={capacity} evidence={capacityFoot} />
        <Stat label="Implementation" value={`${commitments} commitments`} evidence="Accepted Delivery Project quotes" />
        <Stat label="Commercial detail" value="Owner restricted" evidence="Allocation and forecast are the Owner's" />
      </div>
      <PlanAndUsage clientId={clientId} handoffOnly />
    </div>
  );
};
